import * as React from "react";
import { cn } from "../lib/cn";
import { Label } from "./Label";

/**
 * Platform composition helper — wraps Label + control with consistent
 * vertical spacing and an optional hint slot. Not a shadcn primitive
 * (shadcn's `Form` ships with react-hook-form, which is overkill for
 * simple wizards), but pairs naturally with shadcn Inputs / Selects.
 *
 *   <Field label="Markup percentage" hint="0–50%" required>
 *     <Slider value={[pct]} onValueChange={(v) => setPct(v[0])} max={50} />
 *   </Field>
 */
export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  hint?: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
}

export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ label, hint, required, htmlFor, className, children, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-2", className)} {...props}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
        {hint && <span className="ml-2 text-xs font-normal text-muted-foreground">{hint}</span>}
      </Label>
      {children}
    </div>
  ),
);
Field.displayName = "Field";
